// v3.0.0 - 2026-02-02 - Added Sprint 6 Dashboard and Energy Data Browser APIs

import type { ApiResponse, PaginatedResponse, Member, MemberFormData, TICalculation, EnergyData, CER, TICalculationStatus } from '@/types'

// Sprint 6: Admin Dashboard types
export interface AdminDashboardKPIs {
  immessaKwh: number
  prelevataKwh: number
  eaciKwh: number
  tiTotalEur: number
  tiStatus: TICalculationStatus | null
  immessaTrend: number | null
  prelevataTrend: number | null
  eaciTrend: number | null
}

export interface EnergyTrendPoint {
  period: string
  immessa: number
  prelevata: number
  eaci: number
}

export interface RecentUpload {
  id: string
  filename: string
  configId: string
  configCrin: string
  year: number
  month: number
  type: string
  status: string
  rowsParsed: number
  uploadedAt: string
}

export interface RecentCalculation {
  id: string
  configId: string
  configCrin: string
  year: number
  month: number
  eaciKwh: number
  tiTotalEur: number
  status: TICalculationStatus
  calculatedAt: string
}

export interface DashboardFilterOptions {
  configurations: Array<{ id: string; crin: string }>
  availablePeriods: Array<{ year: number; month: number }>
}

export interface DashboardAppliedFilters {
  configId: string | null
  year: number | null
  month: number | null
}

export interface AdminDashboardResponse {
  kpis: AdminDashboardKPIs
  energyTrend: EnergyTrendPoint[]
  recentUploads: RecentUpload[]
  recentCalculations: RecentCalculation[]
  filterOptions: DashboardFilterOptions
  appliedFilters: DashboardAppliedFilters
}

// Sprint 6: Energy Data Browser types
export interface EnergyHourlyItem {
  id: string
  configId: string
  configCrin: string
  memberId: string
  memberName: string
  pod: string
  year: number
  month: number
  day: number
  hour: number
  type: 'immessa' | 'prelevata'
  kwh: number
  createdAt: string
}

export interface EnergyDataBrowserResponse {
  items: EnergyHourlyItem[]
  total: number
  offset: number
  limit: number
  hasMore: boolean
}

export interface EnergyHourlyRecord {
  id: string
  configId: string
  memberId: string
  pod: string
  year: number
  month: number
  day: number
  hour: number
  type: 'immessa' | 'prelevata'
  kwh: number
  createdAt: string
  updatedAt: string | null
}

const API_BASE = '/api'

export class ApiError extends Error {
  status: number
  errorMessage: string

  constructor(status: number, errorMessage: string) {
    super(errorMessage)
    this.name = 'ApiError'
    this.status = status
    this.errorMessage = errorMessage
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('auth_token')

  const response = await fetch(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })

  const data = (await response.json()) as ApiResponse<T>

  if (!data.success) {
    throw new ApiError(response.status, data.error || 'Errore sconosciuto')
  }

  return data.data as T
}

// Legacy API object for backwards compatibility with Sprint 1 code
export const api = {
  // CER
  getCER: (cerId: string) => cer.get(cerId).then(data => ({ success: true, data })).catch(e => ({ success: false, error: e.errorMessage })),

  getCERSettings: (cerId: string) => cer.get(cerId).then(data => ({ success: true, data })).catch(e => ({ success: false, error: e.errorMessage })),

  updateCERSettings: (cerId: string, settings: Record<string, unknown>) =>
    cer.updateSettings(cerId, settings).then(data => ({ success: true, data })).catch(e => ({ success: false, error: e.errorMessage })),

  // Members - returns ApiResponse format for MembersPage compatibility
  getMembers: async (cerId: string, params?: { search?: string; type?: string; status?: string; page?: number }) => {
    try {
      const result = await cer.getMembers(cerId, {
        search: params?.search,
        type: params?.type,
        status: params?.status,
        offset: params?.page ? (params.page - 1) * 10 : 0,
        limit: 10,
      })
      return {
        success: true as const,
        data: {
          items: result.members as unknown as Member[],
          total: result.pagination.total,
          page: params?.page || 1,
          limit: result.pagination.limit,
          hasMore: result.pagination.hasMore,
        },
      }
    } catch (e) {
      return { success: false, error: (e as ApiError).errorMessage } as ApiResponse<PaginatedResponse<Member>>
    }
  },

  getMember: (cerId: string, memberId: string) =>
    request<Member>(`/cer/${cerId}/members/${memberId}`).then(data => ({ success: true, data })).catch(e => ({ success: false, error: e.errorMessage })),

  createMember: (cerId: string, data: MemberFormData) =>
    cer.createMember(cerId, data).then(result => ({ success: true, data: result })).catch(e => ({ success: false, error: e.errorMessage })),

  updateMember: (cerId: string, memberId: string, data: Partial<MemberFormData>) =>
    cer.updateMember(cerId, memberId, data).then(result => ({ success: true, data: result })).catch(e => ({ success: false, error: e.errorMessage })),

  deleteMember: (cerId: string, memberId: string) =>
    cer.deleteMember(cerId, memberId).then(() => ({ success: true, data: undefined })).catch(e => ({ success: false, error: e.errorMessage })),

  // Configurations
  getConfigurations: (cerId: string) =>
    cer.getConfigurations(cerId).then(result => ({ success: true, data: result.configurations })).catch(e => ({ success: false, error: e.errorMessage })),

  // Upload
  uploadFile: (file: File, configId: string) => upload.uploadFile(file, configId),

  processUpload: (uploadId: string) =>
    request(`/upload/process`, {
      method: 'POST',
      body: JSON.stringify({ uploadId }),
    }),

  // Energy Data
  getEnergyData: (configId: string, params?: { period?: string; memberId?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.memberId) searchParams.set('memberId', params.memberId)
    const qs = searchParams.toString()
    return request<EnergyData[]>(
      `/energy-data/${configId}${qs ? `?${qs}` : ''}`
    ).then(data => ({ success: true, data })).catch(e => ({ success: false, error: e.errorMessage }))
  },

  // TI Calculation
  calculateTI: (configId: string, period: string, tiTotal?: number) =>
    ti.calculate({ configId, period, tiTotal: tiTotal || 0, eaciTotal: 0 })
      .then(data => ({ success: true, data }))
      .catch(e => ({ success: false, error: e.errorMessage })),

  getLatestCalculation: (configId: string) =>
    ti.getLatest(configId).then(data => ({ success: true, data })).catch(e => ({ success: false, error: e.errorMessage })),

  approveCalculation: (calculationId: string) =>
    ti.update(calculationId, { status: 'approved' }).then(data => ({ success: true, data })).catch(e => ({ success: false, error: e.errorMessage })),
}

// Auth
export const auth = {
  login: (email: string, password: string) =>
    request<{ token: string; expiresAt: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () =>
    request<void>('/auth/logout', { method: 'POST' }),

  me: () =>
    request<{
      user: {
        id: string
        name: string
        email: string
        role: string
        cerId: string
      }
      cer: {
        id: string
        name: string
        crin: string
        settings: Record<string, unknown>
      }
    }>('/auth/me'),

  requestMagicLink: (email: string) =>
    request<{ message: string }>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyMagicLink: (token: string) =>
    request<{ token: string; expiresAt: string }>(`/auth/verify/${token}`),
}

// CER
export const cer = {
  get: (id: string) =>
    request<CER>(`/cer/${id}`),

  updateSettings: (id: string, settings: Record<string, unknown>) =>
    request<{ settings: Record<string, unknown> }>(`/cer/${id}/settings`, {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    }),

  getMembers: async (
    id: string,
    params?: { search?: string; type?: string; status?: string; role?: string; limit?: number; offset?: number }
  ) => {
    const query = new URLSearchParams()
    if (params?.search) query.set('search', params.search)
    if (params?.type) query.set('type', params.type)
    if (params?.status) query.set('status', params.status)
    if (params?.role) query.set('role', params.role)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))

    const data = await request<{
      items: Array<{
        id: string
        name: string
        email: string
        type: string
        role: string
        status: string
        pod: string | null
        is_company: number
        pnrr_percent: number
        cf: string | null
        censimp: string | null
        created_at: string
        updated_at: string
      }>
      total: number
      page: number
      limit: number
      hasMore: boolean
    }>(`/cer/${id}/members?${query}`)

    return {
      members: data.items.map(m => ({
        id: m.id,
        name: m.name,
        email: m.email,
        type: m.type as 'producer' | 'consumer' | 'prosumer',
        role: m.role as 'admin' | 'member',
        pod: m.pod || '',
        isCompany: m.is_company === 1,
        status: (m.status || 'active') as 'active' | 'inactive',
        pnrrPercent: m.pnrr_percent,
        cf: m.cf,
        censimp: m.censimp,
      })),
      pagination: {
        total: data.total,
        limit: data.limit,
        offset: (data.page - 1) * data.limit,
        hasMore: data.hasMore,
      },
    }
  },

  createMember: (
    cerId: string,
    data: {
      name: string
      email: string
      type: string
      role?: string
      pod?: string
      isCompany?: boolean
      password?: string
      cf?: string
      censimp?: string
      pnrrPercent?: number
    }
  ) =>
    request<{ id: string }>(`/cer/${cerId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateMember: (
    cerId: string,
    memberId: string,
    data: Partial<{
      name: string
      email: string
      type: string
      role: string
      pod: string
      isCompany: boolean
      status: string
      cf: string
      censimp: string
      pnrrPercent: number
    }>
  ) =>
    request<{ id: string }>(`/cer/${cerId}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteMember: (cerId: string, memberId: string) =>
    request<void>(`/cer/${cerId}/members/${memberId}`, { method: 'DELETE' }),

  getMemberDetail: (cerId: string, memberId: string) =>
    request<{
      member: {
        id: string
        name: string
        email: string
        type: 'producer' | 'consumer' | 'prosumer'
        role: 'admin' | 'member'
        pod: string | null
        cf: string | null
        censimp: string | null
        isCompany: boolean
        pnrrPercent: number
        status: 'active' | 'inactive' | 'pending'
        createdAt: string
      }
      energy: {
        totalImmessa: number
        totalPrelevata: number
        totalEaci: number
        lastPeriod: string | null
      }
      tiHistory: Array<{
        calculationId: string
        configId: string
        period: string
        immessaKwh: number
        prelevataKwh: number
        eaciKwh: number
        tiQuota: number
        eccedenzaQuota: number
        costiQuota: number
        netto: number
        status: 'draft' | 'approved' | 'distributed'
        calculatedAt: string
      }>
    }>(`/cer/${cerId}/members/${memberId}/detail`),

  getConfigurations: async (cerId: string) => {
    const data = await request<Array<{
      id: string
      cer_id: string
      crin: string
      cabina_primaria: string | null
      zona: 'NORD' | 'CENTRO' | 'SUD' | null
      status: 'active' | 'inactive' | 'pending'
      created_at: string
      updated_at: string
    }>>(`/cer/${cerId}/configurations`)

    return {
      configurations: data.map(c => ({
        id: c.id,
        name: c.crin,
        crin: c.crin,
        zona: c.zona || 'NORD',
        cabinaPrimaria: c.cabina_primaria,
        memberCount: 0,
        status: c.status,
        createdAt: c.created_at,
        updatedAt: c.updated_at,
      })),
    }
  },

  getConfigurationDetail: (cerId: string, configId: string) =>
    request<{
      configuration: {
        id: string
        crin: string
        zona: 'NORD' | 'CENTRO' | 'SUD' | null
        cabinaPrimaria: string | null
        status: 'active' | 'inactive' | 'pending'
        createdAt: string
        updatedAt: string
      }
      members: Array<{
        id: string
        name: string
        email: string
        type: 'producer' | 'consumer' | 'prosumer'
        pod: string | null
        isCompany: boolean
        pnrrPercent: number
        energy?: {
          totalImmessa: number
          totalPrelevata: number
          tiTotal: number
        }
      }>
      energy: {
        totalImmessa: number
        totalPrelevata: number
        totalEaci: number
        lastPeriod: string | null
      }
      tiHistory: Array<{
        id: string
        period: string
        tiTotal: number
        eaciTotal: number
        status: 'draft' | 'approved' | 'distributed'
        calculatedAt: string
      }>
    }>(`/cer/${cerId}/configurations/${configId}`),

  // Sprint 6: Admin Dashboard with optional filters
  getAdminDashboard: (
    cerId: string,
    filters?: {
      configId?: string
      year?: number
      month?: number
    }
  ) => {
    const query = new URLSearchParams()
    if (filters?.configId) query.set('configId', filters.configId)
    if (filters?.year) query.set('year', String(filters.year))
    if (filters?.month) query.set('month', String(filters.month))
    const qs = query.toString()
    return request<AdminDashboardResponse>(`/cer/${cerId}/admin/dashboard${qs ? `?${qs}` : ''}`)
  },

  // Sprint 6: Energy Data Browser
  getEnergyDataBrowser: (
    cerId: string,
    params?: {
      configId?: string
      memberId?: string
      year?: number
      month?: number
      type?: 'immessa' | 'prelevata'
      pod?: string
      offset?: number
      limit?: number
    }
  ) => {
    const query = new URLSearchParams()
    if (params?.configId) query.set('configId', params.configId)
    if (params?.memberId) query.set('memberId', params.memberId)
    if (params?.year) query.set('year', String(params.year))
    if (params?.month) query.set('month', String(params.month))
    if (params?.type) query.set('type', params.type)
    if (params?.pod) query.set('pod', params.pod)
    if (params?.offset) query.set('offset', String(params.offset))
    if (params?.limit) query.set('limit', String(params.limit))
    return request<EnergyDataBrowserResponse>(`/cer/${cerId}/energy-data?${query}`)
  },
}

// Upload
export const upload = {
  uploadFile: async (file: File, configId?: string, type?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    if (configId) formData.append('configId', configId)
    if (type) formData.append('type', type)

    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })

    const data = (await response.json()) as ApiResponse<{
      id: string
      filename: string
      path: string
      status: string
    }>

    if (!data.success) {
      throw new ApiError(response.status, data.error || 'Errore upload')
    }

    return data.data!
  },

  processFile: (uploadId: string, configId: string, period: string, data: unknown[]) =>
    request<{
      uploadId: string
      configId: string
      period: string
      recordsCreated: number
      recordsUpdated: number
    }>('/upload/process', {
      method: 'POST',
      body: JSON.stringify({ uploadId, configId, period, data }),
    }),
}

// Energy Data
export const energy = {
  getData: (configId: string, params?: { period?: string; summary?: boolean }) => {
    const query = new URLSearchParams()
    if (params?.period) query.set('period', params.period)
    if (params?.summary) query.set('summary', 'true')
    return request<unknown>(`/energy-data/${configId}?${query}`)
  },
}

// Sprint 6: Energy Hourly (single record operations)
export const energyHourly = {
  get: (id: string) =>
    request<EnergyHourlyRecord>(`/energy-hourly/${id}`),

  update: (id: string, kwh: number) =>
    request<EnergyHourlyRecord>(`/energy-hourly/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ kwh }),
    }),
}

// TI Calculation
export const ti = {
  getAll: (params?: {
    configId?: string
    status?: string
    period?: string
    limit?: number
    offset?: number
  }) => {
    const query = new URLSearchParams()
    if (params?.configId) query.set('configId', params.configId)
    if (params?.status) query.set('status', params.status)
    if (params?.period) query.set('period', params.period)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    return request<{
      calculations: TICalculation[]
      total: number
      hasMore: boolean
    }>(`/ti?${query}`)
  },

  calculate: (data: {
    configId: string
    period: string
    tiTotal: number
    eaciTotal: number
    costiGse?: number
    notes?: string
    preview?: boolean
  }) =>
    request<{
      id: string
      configId: string
      period: string
      tiTotal: number
      eaciTotal: number
      quotaCondivisione: number
      soglia: number
      eccedenza: number
      costiGestione: number
      residuo: number
      distribution: unknown[]
      status: string
    }>('/ti/calculate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getLatest: (configId: string) =>
    request<TICalculation>(`/ti/${configId}/latest`),

  getHistory: (configId: string, params?: { status?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    return request<{
      calculations: Array<{
        id: string
        period: string
        tiTotal: number
        status: string
        calculatedAt: string
      }>
      pagination: { total: number; limit: number; offset: number; hasMore: boolean }
    }>(`/ti/${configId}/history?${query}`)
  },

  get: (calcId: string) =>
    request<TICalculation>(`/ti/${calcId}`),

  update: (calcId: string, data: { status?: string; notes?: string }) =>
    request<{ id: string; status: string }>(`/ti/${calcId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (calcId: string) =>
    request<void>(`/ti/${calcId}`, { method: 'DELETE' }),

  downloadReport: async (calcId: string): Promise<Blob> => {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${API_BASE}/ti/${calcId}/report`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) {
      throw new ApiError(response.status, 'Errore download report')
    }
    return response.blob()
  },
}

// Member Portal
export const member = {
  dashboard: () =>
    request<{
      member: {
        id: string
        name: string
        type: string
        pod: string
        isCompany: boolean
      }
      summary: {
        totalEarnings: number
        pendingEarnings: number
        lastPaymentAmount: number
        lastPaymentDate: string | null
      }
      energy: {
        totalImmessa: number
        totalPrelevata: number
        totalEaci: number
        lastPeriod: string | null
      }
      recentCalculations: Array<{
        id: string
        period: string
        netto: number
        status: string
      }>
    }>('/member/dashboard'),

  earnings: () =>
    request<{
      summary: {
        total: number
        pending: number
        lastPayment: { amount: number; date: string } | null
      }
      trend: Array<{ period: string; netto: number }>
      entries: Array<{
        period: string
        tiQuota: number
        eccedenza: number
        netto: number
        status: 'approved' | 'distributed'
        immessa: number
        prelevata: number
        eaci: number
      }>
    }>('/member/earnings'),

  energy: (months?: number) =>
    request<{
      periods: Array<{
        period: string
        immessaKwh: number
        prelevataKwh: number
        eaciKwh: number
      }>
      hourlyAverage: Array<{
        hour: number
        avgImmessa: number
        avgPrelevata: number
      }>
    }>(`/member/energy?months=${months || 12}`),

  documents: (params?: { type?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams()
    if (params?.type) query.set('type', params.type)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    return request<{
      documents: Array<{
        id: string
        docType: 'ti_statement' | '730_cert' | 'contract' | 'report' | 'other'
        title: string
        fileUrl: string
        period: string | null
        fileSize: number | null
        mimeType: string | null
        createdAt: string
      }>
      total: number
    }>(`/member/documents?${query}`)
  },
}

export default api
